import React, { useEffect, useRef } from 'react';
import '../CommonStyle.scss';
import './ReadOnlyField.scss';
import { TextModeView } from '@/components/common/ReadOnlyField/Modes/TextModeView';
import { ListModeView } from '@/components/common/ReadOnlyField/Modes/ListModeView';
import { ObjectValueTypeListView } from './Modes/ObjectValueTypeListView';

type ViewModes = 'text' | 'list' | 'objectValueTypeList';

interface ReadOnlyFieldProps {
  id: string;
  labelTitle: string | null | undefined;
  value?: any | Array<string | number | null | undefined> | null | undefined;
  customValuesSeparator?: string;
  mode?: ViewModes;
  customClassName?: string;
  showChangeIndicator?: boolean;
}

/**
 * @component ReadOnlyField
 * @description Polymorphic container component for read-only data visualization.
 * Orchestrates different rendering strategies ('text', 'list', 'objectValueTypeList')
 * and automatically manages the `read-only-form` state class on the nearest `.form-group`
 * ancestor to ensure form-wide visual consistency.
 *
 * @param {ReadOnlyFieldProps} props - Component properties.
 * @param {string} props.id - Unique identifier for the container root element.
 * @param {string | null | undefined} props.labelTitle - Descriptive title for the field.
 * @param {any} [props.value] - Data to represent (Primitive, Array, or complex structures).
 * @param {ViewModes} [props.mode='text'] - Visualization strategy:
 * - 'text': Flat or concatenated string rendering.
 * - 'list': Itemized block rendering.
 * - 'objectValueTypeList': Specialized key-value rendering.
 * @param {string} [props.customValuesSeparator=' '] - Separator used in 'text' mode for array values.
 * @param {string} [props.customClassName=''] - Additional CSS classes for the main container.
 * @param {boolean} [props.showChangeIndicator] - Indicates if a visual change signal should be displayed.
 * @returns {React.JSX.Element} JSX structure containing the label and the resolved view mode.
 * @example
 * <ReadOnlyField
 * id="user-roles"
 * labelTitle="Assigned Roles"
 * value={['Admin', 'Editor']}
 * mode="list"
 * />
 */
const ReadOnlyField = ({
  id,
  labelTitle,
  value,
  mode = 'text',
  customValuesSeparator = ' ',
  customClassName = '',
}: ReadOnlyFieldProps): React.JSX.Element => {
  const ViewModesMap: Record<ViewModes, () => React.ReactNode> = {
    text: () => TextModeView(value, customValuesSeparator),
    list: () => ListModeView(value),
    objectValueTypeList: () => ObjectValueTypeListView(value),
  };

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const parentFormGroup = containerRef.current?.closest('.form-group');
    if (parentFormGroup) {
      parentFormGroup.classList.add('read-only-form');
      // Cleanup to avoid side effects on component unmount
      return () => parentFormGroup.classList.remove('read-only-form');
    }
  }, []);

  return (
    <>
      <div
        id={id}
        ref={containerRef}
        className={`read-only-container ${customClassName}`}
      >
        <div className={'read-only-label'}>
          <span className={'read-only-label-text'}>{labelTitle}</span>
        </div>
        {ViewModesMap[mode]()}
      </div>
    </>
  );
};

export default ReadOnlyField;
